import { useState, useEffect } from "react";
import {
  Building2,
  Mail,
  Phone,
  MapPin,
  Plus,
  Upload,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Eye,
  Trash2,
  RefreshCw,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { format, differenceInDays, isPast } from "date-fns";
import { fr } from "date-fns/locale";
import type { Subcontractor } from "@/pages/Subcontractors";
import { UnifiedDocumentLinkDialog, type DocConfig } from "@/components/shared/UnifiedDocumentLinkDialog";
import { useDocumentPreview } from "@/contexts/DocumentPreviewContext";

// Configuration des documents prestataires pour UnifiedDocumentLinkDialog
const SUBCONTRACTOR_DOC_CONFIGS: Record<string, DocConfig> = {
  kbis: { key: "kbis", label: "Kbis / Extrait K", doc_type: "administrative", validityDays: 90 },
  urssaf_certificate: { key: "urssaf_certificate", label: "Attestation URSSAF", doc_type: "administrative", validityDays: 180 },
  fiscal_regularity: { key: "fiscal_regularity", label: "Régularité fiscale", doc_type: "administrative", validityDays: 365 },
  rc_pro_insurance: { key: "rc_pro_insurance", label: "Assurance RC Pro", doc_type: "insurance", validityDays: 365 },
  decennial_insurance: { key: "decennial_insurance", label: "Assurance décennale", doc_type: "insurance", validityDays: 365 },
};

interface SubcontractorDocument {
  id: string;
  document_type: string;
  label: string | null;
  valid_from: string | null;
  expires_at: string | null;
  status: string;
  document_id: string | null;
  document?: {
    id: string;
    title: string;
    file_name: string;
    file_path: string;
  } | null;
}

// Required documents for a subcontractor
const REQUIRED_DOCS = [
  { type: "kbis", label: "Kbis / Extrait K", required: true },
  { type: "urssaf_certificate", label: "Attestation URSSAF", required: true },
  { type: "rc_pro_insurance", label: "Assurance RC Pro", required: true },
  { type: "fiscal_regularity", label: "Régularité fiscale", required: false },
  { type: "decennial_insurance", label: "Assurance décennale", required: false },
];

interface SubcontractorDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subcontractor: Subcontractor | null;
  onRefresh: () => void;
}

const SubcontractorDetailSheet = ({
  open,
  onOpenChange,
  subcontractor,
  onRefresh,
}: SubcontractorDetailSheetProps) => {
  const [documents, setDocuments] = useState<SubcontractorDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedDocConfig, setSelectedDocConfig] = useState<DocConfig | null>(null);
  const { openDocument } = useDocumentPreview();

  useEffect(() => {
    if (subcontractor && open) {
      fetchDocuments();
    }
  }, [subcontractor, open]);

  const fetchDocuments = async () => {
    if (!subcontractor) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("subcontractor_documents")
        .select(`
          *,
          document:documents(id, title, file_name, file_path)
        `)
        .eq("subcontractor_id", subcontractor.id);

      if (error) throw error;
      setDocuments(data || []);

      // Recalculate compliance score
      await supabase.rpc("calculate_subcontractor_compliance", {
        p_subcontractor_id: subcontractor.id,
      });
      onRefresh();
    } catch (error) {
      console.error("Error fetching documents:", error);
    } finally {
      setLoading(false);
    }
  };

  const getDocumentByType = (type: string) => {
    return documents.find((d) => d.document_type === type);
  };

  const getStatusInfo = (doc: SubcontractorDocument | undefined) => {
    if (!doc) {
      return {
        status: "missing",
        color: "text-muted-foreground",
        bgColor: "bg-muted",
        icon: Plus,
        label: "Non fourni",
      };
    }

    if (!doc.expires_at) {
      if (doc.status === "pending_verification") {
        return {
          status: "pending",
          color: "text-amber-600",
          bgColor: "bg-amber-500/10",
          icon: Clock,
          label: "À vérifier",
        };
      }
      return {
        status: "valid",
        color: "text-emerald-600",
        bgColor: "bg-emerald-500/10",
        icon: CheckCircle2,
        label: "Valide",
      };
    }

    const expiresAt = new Date(doc.expires_at);
    const daysUntilExpiry = differenceInDays(expiresAt, new Date());

    if (isPast(expiresAt)) {
      return {
        status: "expired",
        color: "text-destructive",
        bgColor: "bg-destructive/10",
        icon: AlertTriangle,
        label: `Expiré`,
      };
    }

    if (daysUntilExpiry <= 30) {
      return {
        status: "expiring",
        color: "text-amber-600",
        bgColor: "bg-amber-500/10",
        icon: Clock,
        label: `Expire dans ${daysUntilExpiry}j`,
      };
    }

    return {
      status: "valid",
      color: "text-emerald-600",
      bgColor: "bg-emerald-500/10",
      icon: CheckCircle2,
      label: "Valide",
    };
  };

  const handleUploadClick = (docType: string) => {
    const config = SUBCONTRACTOR_DOC_CONFIGS[docType];
    if (config) {
      setSelectedDocConfig(config);
      setUploadDialogOpen(true);
    }
  };

  // Use standard DocumentPreviewSheet via context
  const handleViewDocument = async (doc: SubcontractorDocument) => {
    if (!doc.document_id) return;

    try {
      // Fetch the full document from the documents table
      const { data, error } = await supabase
        .from("documents")
        .select("*")
        .eq("id", doc.document_id)
        .single();

      if (error) throw error;
      if (data) {
        openDocument(data);
      }
    } catch (error) {
      console.error("View error:", error);
      toast({
        title: "Erreur",
        description: "Impossible d'ouvrir le document",
        variant: "destructive",
      });
    }
  };

  const handleDeleteDocument = async (docId: string) => {
    try {
      const { error } = await supabase
        .from("subcontractor_documents")
        .delete()
        .eq("id", docId);

      if (error) throw error;

      toast({ title: "Document supprimé" });
      fetchDocuments();
    } catch (error) {
      console.error("Delete error:", error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer le document",
        variant: "destructive",
      });
    }
  };

  if (!subcontractor) return null;

  const complianceColor =
    subcontractor.compliance_score >= 80
      ? "text-emerald-600"
      : subcontractor.compliance_score >= 50
      ? "text-amber-600"
      : "text-destructive";

  const progressColor =
    subcontractor.compliance_score >= 80
      ? "bg-emerald-500"
      : subcontractor.compliance_score >= 50
      ? "bg-amber-500"
      : "bg-destructive";

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              {subcontractor.company_name}
            </SheetTitle>
            <SheetDescription>
              {subcontractor.siret && `SIRET : ${subcontractor.siret}`}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {/* Compliance Score */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">Score de conformité</span>
                  <span className={cn("text-2xl font-bold", complianceColor)}>
                    {subcontractor.compliance_score}%
                  </span>
                </div>
                <Progress value={subcontractor.compliance_score} className="h-2" />
              </CardContent>
            </Card>

            {/* Contact Info */}
            <div className="space-y-3">
              <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                Informations de contact
              </h3>
              {subcontractor.contact_name && (
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="w-4 h-4 text-muted-foreground" />
                  {subcontractor.contact_name}
                </div>
              )}
              {subcontractor.contact_email && (
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <a href={`mailto:${subcontractor.contact_email}`} className="text-primary hover:underline">
                    {subcontractor.contact_email}
                  </a>
                </div>
              )}
              {subcontractor.contact_phone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                  <a href={`tel:${subcontractor.contact_phone}`} className="text-primary hover:underline">
                    {subcontractor.contact_phone}
                  </a>
                </div>
              )}
              {subcontractor.address && (
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  {subcontractor.address}
                </div>
              )}
            </div>

            <Separator />

            {/* Documents */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Documents de conformité
                </h3>
                {loading && <RefreshCw className="w-4 h-4 animate-spin text-muted-foreground" />}
              </div>

              <div className="space-y-3">
                {REQUIRED_DOCS.map(({ type, label, required }) => {
                  const doc = getDocumentByType(type);
                  const statusInfo = getStatusInfo(doc);
                  const StatusIcon = statusInfo.icon;

                  return (
                    <div
                      key={type}
                      className={cn(
                        "flex items-center justify-between p-3 rounded-lg border",
                        !doc && "border-dashed"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div className={cn("p-2 rounded-lg", statusInfo.bgColor)}>
                          <StatusIcon className={cn("w-4 h-4", statusInfo.color)} />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{label}</p>
                          <p className={cn("text-xs", statusInfo.color)}>
                            {statusInfo.label}
                            {doc?.expires_at && statusInfo.status === "valid" && (
                              <span className="text-muted-foreground ml-1">
                                (exp. {format(new Date(doc.expires_at), "dd/MM/yyyy")})
                              </span>
                            )}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-1">
                        {doc ? (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleViewDocument(doc)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleUploadClick(type)}
                            >
                              <Upload className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive"
                              onClick={() => handleDeleteDocument(doc.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleUploadClick(type)}
                          >
                            <Upload className="w-4 h-4 mr-1" />
                            Ajouter
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Notes */}
            {subcontractor.notes && (
              <>
                <Separator />
                <div className="space-y-2">
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                    Notes
                  </h3>
                  <p className="text-sm text-muted-foreground">{subcontractor.notes}</p>
                </div>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {selectedDocConfig && (
        <UnifiedDocumentLinkDialog
          open={uploadDialogOpen}
          onOpenChange={(open) => {
            setUploadDialogOpen(open);
            if (!open) setSelectedDocConfig(null);
          }}
          entityType="subcontractor"
          entityId={subcontractor.id}
          docConfig={selectedDocConfig}
          linkTableName="subcontractor_documents"
          onSuccess={fetchDocuments}
          showValidityDates={!!selectedDocConfig.validityDays}
        />
      )}
    </>
  );
};

export default SubcontractorDetailSheet;